import type { HTMLAttributes, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Icon } from "./Icon";

/** Base surface. `pane` adds the instrument-skin crop-mark hook on primary panes. */
export function Card({
  className,
  pane,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { pane?: boolean }) {
  return (
    <div className={cn("card", className)} data-ds-pane={pane ? "" : undefined} {...rest}>
      {children}
    </div>
  );
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/** Linear progress (0–1). `color` is any token var. */
export function ProgressBar({
  value,
  color = "var(--tr-accent)",
  className,
}: {
  value: number;
  color?: string;
  className?: string;
}) {
  return (
    <div className={cn("pbar", className)}>
      <div
        className="pbar__fill"
        style={{ width: `${Math.round(clamp01(value) * 100)}%`, background: color }}
      />
    </div>
  );
}

export interface ProgressSegment {
  /** Fraction of the whole bar (0–1). */
  value: number;
  color: string;
  /** Dim incomplete/secondary portions. */
  dim?: boolean;
}

/** Segmented progress — composition in one bar (approved / review / todo). */
export function SegmentedProgress({
  segments,
  className,
}: {
  segments: ProgressSegment[];
  className?: string;
}) {
  return (
    <div className={cn("pbar pbar--seg", className)}>
      {segments.map((s, i) => (
        <div
          key={i}
          style={{
            width: `${clamp01(s.value) * 100}%`,
            background: s.color,
            opacity: s.dim ? 0.5 : 1,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Grid row for work queues. Pass `columns` (a grid-template-columns string)
 * shared by head + body rows. `current` marks the row being worked on.
 */
export function ListRow({
  columns,
  head,
  current,
  className,
  style,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement> & {
  columns?: string;
  head?: boolean;
  current?: boolean;
}) {
  return (
    <div
      className={cn("lrow", head && "lrow--head", current && "lrow--current", className)}
      style={{ gridTemplateColumns: columns, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Calm, instructive placeholder: icon + one line + one primary action. */
export function EmptyState({
  icon,
  title,
  subtitle,
  action,
  className,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("empty", className)}>
      {icon && <Icon icon={icon} size={26} className="empty__icon" />}
      <div className="empty__t">{title}</div>
      {subtitle && <div className="empty__s">{subtitle}</div>}
      {action}
    </div>
  );
}
